import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { ProjectService } from '../project.service';
import { BreadcrumbComponent, BreadcrumbCrumb } from '../../../shared/breadcrumb/breadcrumb.component';
import { IssueWorkflowApiService } from '../../issue-workflows/issue-workflow.service';
import { PmReportsApiService } from '../../pm-reports/pm-reports.service';
import { PmAdvancedApiService } from '../../pm-advanced/pm-advanced.service';
import { ProjectDashboardDto, DeadlineDashboardDto, ProjectSlaDto, ProjectDeadlineHealthDto } from '../../pm-reports/pm-reports.models';
import { healthRagClass, velocityTrendLabel, velocityTrendClass, formatPercent, normalizeProjectDashboard, extractHealthMetrics } from '../../shared/health.utils';
import { ProjectMilestoneDto, RoadmapDto } from '../../pm-advanced/pm-advanced.models';
import { PmTimelineViewComponent } from '../../pm-advanced/pm-timeline-view.component';
import { IssueWorkflowDto } from '../../issue-workflows/issue-workflow.models';
import {
  ProjectDetailDto,
  ProjectMemberDto,
  ProjectActivityDto,
  AddMemberRequest,
  UpdateMemberRequest,
  PROJECT_STATUS_LABELS,
  PROJECT_PRIORITY_LABELS,
  PROJECT_MEMBER_ROLES
} from '../project.models';
import { ApiService } from '../../../core/services/api.service';

interface EmployeeOption {
  id: string;
  name: string;
  email?: string;
  jobTitle?: string;
}

interface TeamLeadOption {
  leadEmployeeId: string;
  leadName: string;
  teamName: string;
  kind: number;
  kindLabel?: string;
}

interface ProjectTaskRow {
  id: string;
  title: string;
  status: number;
  statusLabel?: string;
  priority: number;
  dueDate?: string;
  teamLeadId?: string;
  teamLeadName?: string;
  assigneeId?: string;
  isOverdue?: boolean;
  bugCount?: number;
}

@Component({
  selector: 'app-project-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatDividerModule,
    MatMenuModule,
    MatTableModule,
    MatSlideToggleModule,
    BreadcrumbComponent,
    PmTimelineViewComponent
  ],
  templateUrl: './project-detail.component.html',
  styleUrl: './project-detail.component.scss'
})
export class ProjectDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly projectService = inject(ProjectService);
  private readonly workflowsApi = inject(IssueWorkflowApiService);
  private readonly reportsApi = inject(PmReportsApiService);
  private readonly pmAdvanced = inject(PmAdvancedApiService);
  private readonly api = inject(ApiService);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly project = signal<ProjectDetailDto | null>(null);
  readonly activity = signal<ProjectActivityDto[]>([]);
  readonly loadingActivity = signal(false);
  readonly togglingFavorite = signal(false);
  readonly archiving = signal(false);
  readonly duplicating = signal(false);
  readonly settingTemplate = signal(false);

  readonly employees = signal<EmployeeOption[]>([]);
  readonly addingMember = signal(false);
  newMemberEmployeeId = '';
  newMemberRole = 'Member';
  readonly memberRoles = PROJECT_MEMBER_ROLES;

  readonly tasks = signal<ProjectTaskRow[]>([]);
  readonly teamLeads = signal<TeamLeadOption[]>([]);
  readonly projectWorkflows = signal<IssueWorkflowDto[]>([]);
  readonly assigningWorkflow = signal(false);
  readonly loadingTasks = signal(false);
  readonly creatingTask = signal(false);
  readonly reportingBug = signal(false);
  readonly projectDashboard = signal<ProjectDashboardDto | null>(null);
  readonly projectHealthMetrics = signal<ProjectDeadlineHealthDto | null>(null);
  readonly loadingDashboard = signal(false);
  readonly deadlineDash = signal<DeadlineDashboardDto | null>(null);
  readonly projectSla = signal<ProjectSlaDto | null>(null);
  readonly roadmap = signal<RoadmapDto | null>(null);
  readonly loadingRoadmap = signal(false);
  readonly savingMilestone = signal(false);
  readonly selectedTabIndex = signal(0);
  showCreateTask = false;
  showReportBug = false;

  newTaskTitle = '';
  newTaskDescription = '';
  newTaskTeamLeadId = '';
  newTaskTeamKind: number | '' = '';
  newTaskPriority = 1;
  newTaskStartDate = '';
  newTaskDueDate = '';

  bugTaskId = '';
  bugTitle = '';
  bugDescription = '';
  bugSeverity = 1;
  bugCategory = 0;
  bugSteps = '';
  selectedWorkflowId = '';

  milestoneName = '';
  milestoneDescription = '';
  milestoneTargetDate = '';
  milestoneVersionId = '';

  readonly taskPriorityOptions = [
    { value: 0, label: 'Low' },
    { value: 1, label: 'Medium' },
    { value: 2, label: 'High' },
    { value: 3, label: 'Critical' }
  ];
  readonly teamKindOptions = [
    { value: 0, label: 'Developer' },
    { value: 1, label: 'QA' },
    { value: 2, label: 'Designer' },
    { value: 3, label: 'ASO' }
  ];
  readonly bugSeverityOptions = [
    { value: 0, label: 'Trivial' },
    { value: 1, label: 'Minor' },
    { value: 2, label: 'Major' },
    { value: 3, label: 'Critical' }
  ];
  readonly bugCategoryOptions = [
    { value: 0, label: 'Functional' },
    { value: 1, label: 'Crash' },
    { value: 2, label: 'UI/UX' },
    { value: 3, label: 'Performance' }
  ];

  readonly statusLabels = PROJECT_STATUS_LABELS;
  readonly priorityLabels = PROJECT_PRIORITY_LABELS;

  readonly statusColorMap: Record<number, string> = {
    0: 'status-planning', 1: 'status-active', 2: 'status-onhold',
    3: 'status-completed', 4: 'status-cancelled', 5: 'status-archived'
  };

  readonly memberColumns = ['name', 'role', 'joinedAt', 'status', 'actions'];
  readonly taskColumns = ['title', 'status', 'teamLead', 'priority', 'deadline', 'bugs', 'actions'];

  get projectId(): string {
    return this.route.snapshot.paramMap.get('id') ?? '';
  }

  ngOnInit(): void {
    this.applyTabQueryParams();
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(() => this.applyTabQueryParams());

    this.loadProject();
    this.loadEmployees();
    this.loadTeamLeads();
    this.loadTasks();
    this.loadActivity();
    this.loadProjectDashboard();
    this.loadDeadlineWidgets();
    this.loadRoadmap();
  }

  private applyTabQueryParams(): void {
    const tab = (this.route.snapshot.queryParamMap.get('tab') ?? '').toLowerCase();
    const tabIndex: Record<string, number> = {
      overview: 0,
      activity: 1,
      members: 2,
      tasks: 3,
      dashboard: 4,
      roadmap: 5
    };
    if (tab in tabIndex) {
      this.selectedTabIndex.set(tabIndex[tab]);
    }
    if (this.route.snapshot.queryParamMap.get('addTask') === '1') {
      this.selectedTabIndex.set(3);
      this.showCreateTask = true;
    }
  }

  onTabChange(index: number): void {
    this.selectedTabIndex.set(index);
  }

  openAddTaskForm(): void {
    this.selectedTabIndex.set(3);
    this.showCreateTask = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProject(): void {
    this.loading.set(true);
    this.error.set(null);
    this.projectService.getById(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (project) => {
        this.project.set(project);
        this.selectedWorkflowId = project.issueWorkflowId ?? '';
        this.loading.set(false);
        this.loadProjectWorkflows(project.id);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load project.');
        this.loading.set(false);
      }
    });
  }

  loadEmployees(): void {
    this.api.getPaged<EmployeeOption>('v1/employees', { page: 1, pageSize: 200 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.employees.set((page.items ?? []).map((e: any) => ({
            id: String(e.id ?? e.Id),
            name: e.fullName ?? e.FullName ?? `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim(),
            email: e.email ?? e.Email,
            jobTitle: e.jobTitle ?? e.JobTitle
          })));
        },
        error: () => this.employees.set([])
      });
  }

  loadTeamLeads(): void {
    this.api.get<any>('v1/tasks/allocation/leads').pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const items = Array.isArray(res) ? res : (res?.items ?? res?.Items ?? []);
        this.teamLeads.set(items.map((x: any) => ({
          leadEmployeeId: String(x.leadEmployeeId ?? x.LeadEmployeeId),
          leadName: x.leadName ?? x.LeadName ?? 'Team lead',
          teamName: x.teamName ?? x.TeamName ?? x.name ?? x.Name ?? '',
          kind: Number(x.kind ?? x.Kind ?? 0),
          kindLabel: x.kindLabel ?? x.KindLabel
        })));
      },
      error: () => this.teamLeads.set([])
    });
  }

  loadTasks(): void {
    this.loadingTasks.set(true);
    this.api.getPaged<ProjectTaskRow>('v1/tasks', { page: 1, pageSize: 100, projectId: this.projectId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          const items = (page.items ?? []).map((t: any) => ({
            id: String(t.id ?? t.Id),
            title: t.title ?? t.Title,
            status: Number(t.status ?? t.Status ?? 0),
            statusLabel: t.statusLabel ?? t.StatusLabel,
            priority: Number(t.priority ?? t.Priority ?? 1),
            dueDate: t.dueDate ?? t.DueDate,
            teamLeadId: t.teamLeadId ?? t.TeamLeadId,
            teamLeadName: t.teamLeadName ?? t.TeamLeadName,
            assigneeId: t.assigneeId ?? t.AssigneeId,
            isOverdue: !!(t.isOverdue ?? t.IsOverdue),
            bugCount: Number(t.openBugCount ?? t.OpenBugCount ?? t.bugCount ?? t.BugCount ?? 0)
          }));
          this.tasks.set(items);
          this.loadingTasks.set(false);
        },
        error: (err) => {
          this.error.set(err?.message || 'Failed to load tasks.');
          this.loadingTasks.set(false);
        }
      });
  }

  createTask(): void {
    if (!this.newTaskTitle.trim()) {
      this.error.set('Task title is required.');
      return;
    }
    if (!this.newTaskDueDate) {
      this.error.set('Deadline is required.');
      return;
    }
    if (!this.newTaskTeamLeadId) {
      this.error.set('Team lead is required.');
      return;
    }
    if (this.newTaskStartDate && this.newTaskDueDate < this.newTaskStartDate) {
      this.error.set('Deadline cannot be earlier than start date.');
      return;
    }

    this.creatingTask.set(true);
    this.error.set(null);
    const body: Record<string, unknown> = {
      projectId: this.projectId,
      title: this.newTaskTitle.trim(),
      description: this.newTaskDescription.trim() || undefined,
      teamLeadId: this.newTaskTeamLeadId,
      priority: this.newTaskPriority,
      dueDate: this.newTaskDueDate,
      startDate: this.newTaskStartDate || undefined
    };
    if (this.newTaskTeamKind !== '') body['teamKind'] = this.newTaskTeamKind;

    this.api.post('v1/tasks', body).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.creatingTask.set(false);
        this.showCreateTask = false;
        this.newTaskTitle = '';
        this.newTaskDescription = '';
        this.newTaskTeamLeadId = '';
        this.newTaskTeamKind = '';
        this.newTaskPriority = 1;
        this.newTaskStartDate = '';
        this.newTaskDueDate = '';
        this.loadTasks();
        this.loadProject();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to create task.');
        this.creatingTask.set(false);
      }
    });
  }

  openReportBug(task: ProjectTaskRow): void {
    this.bugTaskId = task.id;
    this.bugTitle = '';
    this.bugDescription = '';
    this.bugSeverity = 1;
    this.bugCategory = 0;
    this.bugSteps = '';
    this.showReportBug = true;
  }

  reportBug(): void {
    if (!this.bugTaskId) {
      this.error.set('Select a task to report the bug against.');
      return;
    }
    if (!this.bugTitle.trim()) {
      this.error.set('Bug title is required.');
      return;
    }

    this.reportingBug.set(true);
    this.error.set(null);
    this.api.post('v1/bugs', {
      projectId: this.projectId,
      relatedTaskId: this.bugTaskId,
      title: this.bugTitle.trim(),
      description: this.bugDescription.trim() || undefined,
      severity: this.bugSeverity,
      category: this.bugCategory,
      stepsToReproduce: this.bugSteps.trim() || undefined
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.reportingBug.set(false);
        this.showReportBug = false;
        this.loadTasks();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to report bug.');
        this.reportingBug.set(false);
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/projects']);
  }

  editProject(): void {
    this.router.navigate(['/projects', this.projectId, 'edit']);
  }

  loadActivity(): void {
    this.loadingActivity.set(true);
    this.projectService.getActivity(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => {
        this.activity.set(Array.isArray(items) ? items : []);
        this.loadingActivity.set(false);
      },
      error: () => {
        this.activity.set([]);
        this.loadingActivity.set(false);
      }
    });
  }

  toggleFavorite(): void {
    const p = this.project();
    if (!p || this.togglingFavorite()) return;
    this.togglingFavorite.set(true);
    this.projectService.toggleFavorite(p.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.togglingFavorite.set(false);
        this.loadProject();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to update favorite.');
        this.togglingFavorite.set(false);
      }
    });
  }

  archiveProject(): void {
    const p = this.project();
    if (!p || p.archivedAt || this.archiving()) return;
    if (!confirm(`Archive project "${p.name}"? It will be marked as archived.`)) return;
    this.archiving.set(true);
    this.projectService.archive(p.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (updated) => {
        this.project.set(updated);
        this.archiving.set(false);
        this.loadActivity();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to archive project.');
        this.archiving.set(false);
      }
    });
  }

  restoreProject(): void {
    const p = this.project();
    if (!p || !p.archivedAt || this.archiving()) return;
    if (!confirm(`Restore project "${p.name}"?`)) return;
    this.archiving.set(true);
    this.projectService.restore(p.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (updated) => {
        this.project.set(updated);
        this.archiving.set(false);
        this.loadActivity();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to restore project.');
        this.archiving.set(false);
      }
    });
  }

  duplicateProject(): void {
    const p = this.project();
    if (!p || this.duplicating()) return;
    if (!confirm(`Duplicate project "${p.name}"?`)) return;
    this.duplicating.set(true);
    this.projectService.duplicate(p.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (created) => {
        this.duplicating.set(false);
        this.router.navigate(['/projects', created.id]);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to duplicate project.');
        this.duplicating.set(false);
      }
    });
  }

  toggleTemplate(): void {
    const p = this.project();
    if (!p || p.archivedAt || this.settingTemplate()) return;
    const next = !p.isTemplate;
    const label = next ? 'Mark as template' : 'Unmark as template';
    if (!confirm(`${label} for "${p.name}"?`)) return;
    this.settingTemplate.set(true);
    this.projectService.setAsTemplate(p.id, next).pipe(takeUntil(this.destroy$)).subscribe({
      next: (updated) => {
        this.project.set(updated);
        this.settingTemplate.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to update template flag.');
        this.settingTemplate.set(false);
      }
    });
  }

  addMember(): void {
    if (!this.newMemberEmployeeId) return;
    this.addingMember.set(true);

    const request: AddMemberRequest = {
      employeeId: this.newMemberEmployeeId,
      role: this.newMemberRole
    };

    this.projectService.addMember(this.projectId, request).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.newMemberEmployeeId = '';
        this.newMemberRole = 'Member';
        this.addingMember.set(false);
        this.loadProject();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to add member.');
        this.addingMember.set(false);
      }
    });
  }

  updateMemberRole(member: ProjectMemberDto, newRole: string): void {
    const request: UpdateMemberRequest = { role: newRole, isActive: member.isActive };
    this.projectService.updateMember(this.projectId, member.id, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadProject(),
        error: (err) => this.error.set(err?.message || 'Failed to update member.')
      });
  }

  toggleMemberActive(member: ProjectMemberDto): void {
    const request: UpdateMemberRequest = { role: member.role, isActive: !member.isActive };
    this.projectService.updateMember(this.projectId, member.id, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadProject(),
        error: (err) => this.error.set(err?.message || 'Failed to update member status.')
      });
  }

  releaseCalendarLink(): string[] {
    return ['/pm-advanced'];
  }

  releaseCalendarQueryParams(): { projectId: string; tab: string } {
    return { projectId: this.projectId, tab: 'calendar' };
  }

  deploymentLabel(status?: number, label?: string): string {
    const options = [
      { value: 0, label: 'Not deployed' },
      { value: 1, label: 'Staging' },
      { value: 2, label: 'Production' },
      { value: 3, label: 'Rolled back' }
    ];
    if (label) return label;
    return options.find(d => d.value === status)?.label ?? '—';
  }

  removeMember(member: ProjectMemberDto): void {
    if (!confirm(`Remove ${member.employeeName} from this project?`)) return;
    this.projectService.removeMember(this.projectId, member.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadProject(),
        error: (err) => this.error.set(err?.message || 'Failed to remove member.')
      });
  }

  availableEmployees(): EmployeeOption[] {
    const project = this.project();
    if (!project) return this.employees();
    const existingIds = new Set(project.members.map(m => m.employeeId));
    return this.employees().filter(e => !existingIds.has(e.id));
  }

  getProgressColor(progress: number): string {
    if (progress >= 80) return 'success';
    if (progress >= 40) return 'warning';
    return 'danger';
  }

  priorityLabel(priority: number): string {
    return this.taskPriorityOptions.find(p => p.value === priority)?.label ?? String(priority);
  }

  filteredLeads(): TeamLeadOption[] {
    if (this.newTaskTeamKind === '') return this.teamLeads();
    return this.teamLeads().filter(l => l.kind === this.newTaskTeamKind);
  }

  loadProjectWorkflows(projectId: string): void {
    this.workflowsApi.list(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.projectWorkflows.set(Array.isArray(items) ? items : []),
      error: () => this.projectWorkflows.set([])
    });
  }

  assignWorkflow(): void {
    if (this.assigningWorkflow()) return;
    this.assigningWorkflow.set(true);
    const workflowId = this.selectedWorkflowId || null;
    this.projectService.assignWorkflow(this.projectId, workflowId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (project) => {
          this.project.set(project);
          this.selectedWorkflowId = project.issueWorkflowId ?? '';
          this.assigningWorkflow.set(false);
        },
        error: (err) => {
          this.error.set(err?.message || 'Failed to assign workflow. The endpoint may not be available yet.');
          this.assigningWorkflow.set(false);
        }
      });
  }

  workflowDisplayName(): string {
    const p = this.project();
    if (p?.issueWorkflowName) return p.issueWorkflowName;
    if (p?.issueWorkflowId) {
      const wf = this.projectWorkflows().find(w => w.id === p.issueWorkflowId);
      return wf?.name ?? p.issueWorkflowId;
    }
    return 'Not assigned';
  }

  loadProjectDashboard(): void {
    this.loadingDashboard.set(true);
    this.reportsApi.getProjectDashboard(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (dash) => {
        this.projectDashboard.set(normalizeProjectDashboard(dash));
        this.loadingDashboard.set(false);
      },
      error: () => {
        this.projectDashboard.set(this.buildFallbackDashboard());
        this.loadingDashboard.set(false);
      }
    });
    this.reportsApi.projectHealth(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (h) => this.projectHealthMetrics.set(h),
      error: () => this.projectHealthMetrics.set(null)
    });
  }

  loadDeadlineWidgets(): void {
    this.reportsApi.deadlineDashboard(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (d) => this.deadlineDash.set(d),
      error: () => this.deadlineDash.set(null)
    });
    this.reportsApi.projectSla(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (s) => this.projectSla.set(s),
      error: () => this.projectSla.set(null)
    });
  }

  private buildFallbackDashboard(): ProjectDashboardDto {
    const p = this.project();
    const s = p?.tasksSummary;
    return {
      projectId: this.projectId,
      projectName: p?.name,
      completionPercent: p?.progress ?? 0,
      openIssues: s?.open,
      completedIssues: s?.done,
      doneIssues: s?.done,
      blockedIssues: 0,
      overdueIssues: 0,
      healthLabel: p ? this.statusLabels[p.status] : undefined
    };
  }

  loadRoadmap(): void {
    this.loadingRoadmap.set(true);
    this.pmAdvanced.getRoadmap(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.roadmap.set(data);
        this.loadingRoadmap.set(false);
      },
      error: () => {
        this.pmAdvanced.listVersions(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
          next: (versions) => {
            this.roadmap.set({
              projectId: this.projectId,
              versions: Array.isArray(versions) ? versions : [],
              milestones: []
            });
            this.loadingRoadmap.set(false);
          },
          error: () => {
            this.roadmap.set(null);
            this.loadingRoadmap.set(false);
          }
        });
      }
    });
  }

  createMilestone(): void {
    if (!this.milestoneName.trim()) {
      this.error.set('Milestone name is required.');
      return;
    }
    this.savingMilestone.set(true);
    this.pmAdvanced.createMilestone({
      projectId: this.projectId,
      name: this.milestoneName.trim(),
      description: this.milestoneDescription.trim() || undefined,
      targetDate: this.milestoneTargetDate || undefined,
      versionId: this.milestoneVersionId || undefined
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.milestoneName = '';
        this.milestoneDescription = '';
        this.milestoneTargetDate = '';
        this.milestoneVersionId = '';
        this.savingMilestone.set(false);
        this.loadRoadmap();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to create milestone.');
        this.savingMilestone.set(false);
      }
    });
  }

  deleteMilestone(m: ProjectMilestoneDto): void {
    if (!confirm(`Delete milestone "${m.name}"?`)) return;
    this.pmAdvanced.deleteMilestone(m.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadRoadmap(),
      error: (err) => this.error.set(err?.message || 'Failed to delete milestone.')
    });
  }

  reportsLink(): string[] {
    return ['/pm-reports'];
  }

  reportsQueryParams(): { projectId: string } {
    return { projectId: this.projectId };
  }

  projectBreadcrumbs(project: ProjectDetailDto): BreadcrumbCrumb[] {
    return [
      { label: 'Projects', route: '/projects' },
      { label: project.name }
    ];
  }

  dashboardHealthMetrics(): ReturnType<typeof extractHealthMetrics> {
    const dash = this.projectDashboard();
    const health = this.projectHealthMetrics();
    if (health) return extractHealthMetrics(health);
    if (dash) return extractHealthMetrics(dash as unknown as Record<string, unknown>, dash.health);
    return {};
  }

  healthRagClass = healthRagClass;
  velocityTrendLabel = velocityTrendLabel;
  velocityTrendClass = velocityTrendClass;
  formatPercent = formatPercent;
}
